import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Card,
  Badge,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { alertActions } from '../../redux/_actions';
import { generateSessionCardData } from '../../utilities/generate-session-url';
import { format } from 'date-fns';
import { MEDIAURL, SCORESURL, V_LOGO } from '../../utilities/constants';
import GetSiteSettings from '../../apollo/queries/GetSiteSettings.graphql';
import CreateSiteSettings from '../../apollo/mutations/CreateSiteSettings.graphql';
import UpdateSiteSettings from '../../apollo/mutations/UpdateSiteSettings.graphql';
import { API, graphqlOperation } from 'aws-amplify';
import ToastStack from '../session/toaststack';
import './homepagebuilder.css';

// Settings IDs - each season has its own record named "Season X"
// The global "homepage" record stores which season is active
const getSeasonSettingsId = (seasonNum) => `Season ${seasonNum}`;
const GLOBAL_SETTINGS_ID = 'homepage';

// GraphQL query to get session by key with team data
const getSessionByKey = /* GraphQL */ `
  query GetSessionByKey($sessionKey: String!) {
    SessionByKey(sessionKey: $sessionKey, limit: 1) {
      items {
        id
        sessionKey
        name
        startAt
        gender
        sessionTeams {
          items {
            id
            order
            _deleted
            team {
              id
              name
              logos
              altNames
              _deleted
            }
          }
        }
        streams {
          items {
            id
            type
            title
            index
          }
        }
      }
    }
  }
`;

const logoPath = (teamLogo) => {
  if (teamLogo) {
    return MEDIAURL + teamLogo;
  }
  return V_LOGO;
};

const defaultHomepageData = {
  weeks: [],
};

// Helper to parse homepageData which may be double-stringified
// (DynamoDB stores a JSON string, GraphQL wraps it in another string)
const parseHomepageData = (data) => {
  if (!data) return defaultHomepageData;
  try {
    let parsed = typeof data === 'string' ? JSON.parse(data) : data;
    // Check if we got another string (double-stringified case)
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    return parsed?.weeks ? parsed : defaultHomepageData;
  } catch (e) {
    console.error('Error parsing homepageData:', e);
    return defaultHomepageData;
  }
};

function HomepageBuilder() {
  const dispatch = useDispatch();
  const [homepageData, setHomepageData] = useState(defaultHomepageData);
  const [currentSeason, setCurrentSeason] = useState(6);
  const [activeSeason, setActiveSeason] = useState(6); // The season shown on homepage
  const [seasonLabel, setSeasonLabel] = useState('Season 6');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [settingsExist, setSettingsExist] = useState(false);
  const [settingsVersion, setSettingsVersion] = useState(null);
  const [availableSeasons] = useState([6, 5, 4]); // Seasons with data (newest first)
  const [isLoadingSeason, setIsLoadingSeason] = useState(false);

  // Batch import state
  const [batchInput, setBatchInput] = useState('');
  const [batchParsedRows, setBatchParsedRows] = useState([]);
  const [isBatchLoading, setIsBatchLoading] = useState(false);
  const [batchTargetWeek, setBatchTargetWeek] = useState(0);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [weekOrderReversed, setWeekOrderReversed] = useState(false);
  const [statusFilters, setStatusFilters] = useState(['live']); // 'live', 'preview', 'draft'

  // Load global settings first (to get active season)
  const { data: globalData, loading: loadingGlobal } = useQuery(
    GetSiteSettings,
    {
      variables: { id: GLOBAL_SETTINGS_ID },
      fetchPolicy: 'network-only',
    }
  );

  // Load current season's settings
  const { data: settingsData, loading: loadingSettings, refetch } = useQuery(
    GetSiteSettings,
    {
      variables: { id: getSeasonSettingsId(currentSeason) },
      fetchPolicy: 'network-only',
      skip: !currentSeason,
    }
  );

  const [createSettings] = useMutation(CreateSiteSettings);
  const [updateSettings] = useMutation(UpdateSiteSettings);

  // Load global settings to get active season
  useEffect(() => {
    if (globalData?.getSiteSettings) {
      const settings = globalData.getSiteSettings;
      if (settings.currentSeason) {
        setActiveSeason(settings.currentSeason);
        // Start with the active season selected
        if (!settingsData) {
          setCurrentSeason(settings.currentSeason);
        }
      }
    }
  }, [globalData]);

  // Load season-specific settings into state
  useEffect(() => {
    // Check if the loaded data matches the season we're trying to load
    const expectedId = getSeasonSettingsId(currentSeason);
    const loadedId = settingsData?.getSiteSettings?.id;

    if (settingsData?.getSiteSettings && loadedId === expectedId) {
      const settings = settingsData.getSiteSettings;
      setSettingsExist(true);
      setSettingsVersion(settings._version);
      setSeasonLabel(settings.seasonLabel || `Season ${currentSeason}`);
      setHomepageData(parseHomepageData(settings.homepageData));
      setIsLoadingSeason(false);
    } else if (!loadingSettings && !loadingGlobal && loadedId !== expectedId) {
      // Data is for wrong season, or no data - wait or show empty
      if (!settingsData?.getSiteSettings) {
        // No data for this season yet
        setSettingsExist(false);
        setSettingsVersion(null);
        setSeasonLabel(`Season ${currentSeason}`);
        setHomepageData(defaultHomepageData);
        setIsLoadingSeason(false);
      }
    }
  }, [settingsData, loadingSettings, loadingGlobal, currentSeason]);

  // Switch to a different season
  const switchSeason = async (newSeason) => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Switch anyway?')) {
        return;
      }
    }
    // Reset state
    setIsLoadingSeason(true);
    setHasChanges(false);
    setSettingsExist(false);
    setSettingsVersion(null);
    setCurrentSeason(newSeason);
    setSeasonLabel(`Season ${newSeason}`);
    setHomepageData(defaultHomepageData);
    // Force refetch with new season ID and handle result directly
    try {
      const seasonId = getSeasonSettingsId(newSeason);
      const result = await refetch({ id: seasonId });
      const settings = result?.data?.getSiteSettings;
      if (settings) {
        setSettingsExist(true);
        setSettingsVersion(settings._version);
        setSeasonLabel(settings.seasonLabel || `Season ${newSeason}`);
        setHomepageData(parseHomepageData(settings.homepageData));
      }
      setIsLoadingSeason(false);
    } catch (e) {
      console.error('Error fetching season:', e);
      setIsLoadingSeason(false);
    }
  };

  // Generate match card from session
  const generateMatchCard = (session) => {
    if (!session) return null;

    const teams =
      session.sessionTeams?.items
        ?.filter((st) => !st._deleted && st.team && !st.team._deleted)
        ?.sort((a, b) => a.order - b.order)
        ?.map((st) => st.team) || [];

    const cardData = generateSessionCardData(session, teams);

    // Add streaming tag only if session has a main stream (PRODUCTION with title "Main" or "Official")
    const productionStreams =
      session.streams?.items?.filter(
        (s) => !s._deleted && s.type === 'PRODUCTION'
      ) || [];
    const hasMainStream = productionStreams.some(
      (s) => s.title === 'Main' || s.title === 'Official'
    );
    if (hasMainStream) {
      cardData.tags = [...(cardData.tags || []), 'STREAMING'];
    }

    // Build multiStream from non-main production streams (individual apparatus streams)
    const individualStreams = productionStreams
      .filter((s) => s.title && s.title !== 'Main' && s.title !== 'Official')
      .sort((a, b) => (a.index ?? 999) - (b.index ?? 999));
    if (individualStreams.length > 0) {
      const multiStream = {};
      individualStreams.forEach((s) => {
        multiStream[s.index ?? s.title] = s.title;
      });
      cardData.multiStream = multiStream;
    }

    return cardData;
  };

  // Remove match from week
  const removeMatch = (weekIndex, matchIndex) => {
    const newData = { ...homepageData };
    newData.weeks[weekIndex].matches.splice(matchIndex, 1);
    setHomepageData(newData);
    setHasChanges(true);
  };

  // Refresh a single match by re-fetching session data
  const refreshMatch = async (weekIndex, matchIndex) => {
    const match = homepageData.weeks[weekIndex]?.matches[matchIndex];
    if (!match?.sessionId) return;

    try {
      const result = await API.graphql(
        graphqlOperation(getSessionByKey, { sessionKey: match.sessionId })
      );
      const session = result.data?.SessionByKey?.items?.[0];
      if (session) {
        const updatedCard = generateMatchCard(session);
        if (updatedCard) {
          // Check if anything actually changed
          const oldJson = JSON.stringify(match);
          const newJson = JSON.stringify(updatedCard);

          if (oldJson === newJson) {
            dispatch(alertActions.notification('No changes detected'));
            return;
          }

          const newData = { ...homepageData };
          newData.weeks[weekIndex].matches[matchIndex] = updatedCard;
          setHomepageData(newData);
          setHasChanges(true);
          dispatch(alertActions.success('Match updated with new data'));
        }
      } else {
        dispatch(alertActions.notification('Session not found'));
      }
    } catch (e) {
      console.error('Error refreshing match:', e);
      dispatch(alertActions.error('Error refreshing match'));
    }
  };

  // Add new week
  const addWeek = () => {
    const newData = { ...homepageData };
    const weekNum = newData.weeks.length + 1;
    newData.weeks.push({
      label: `Week ${weekNum}`,
      matches: [],
      status: 'draft', // draft | preview | live
    });
    setHomepageData(newData);
    setHasChanges(true);
    // Activate draft filter so the new week is visible
    if (!statusFilters.includes('draft')) {
      setStatusFilters((prev) => [...prev, 'draft']);
    }
  };

  // Cycle week status: draft -> preview -> live -> draft
  const cycleWeekStatus = (weekIndex) => {
    const newData = { ...homepageData };
    const week = newData.weeks[weekIndex];
    // Handle legacy 'published' field
    const currentStatus = week.status || (week.published ? 'live' : 'draft');
    const nextStatus =
      currentStatus === 'draft'
        ? 'preview'
        : currentStatus === 'preview'
        ? 'live'
        : 'draft';
    newData.weeks[weekIndex].status = nextStatus;
    delete newData.weeks[weekIndex].published; // Remove legacy field
    setHomepageData(newData);
    // Activate the new status filter so the week remains visible
    if (!statusFilters.includes(nextStatus)) {
      setStatusFilters((prev) => [...prev, nextStatus]);
    }
    setHasChanges(true);
  };

  // Remove week
  const removeWeek = (weekIndex) => {
    const newData = { ...homepageData };
    newData.weeks.splice(weekIndex, 1);
    setHomepageData(newData);
    setHasChanges(true);
  };

  // Update week label
  const updateWeekLabel = (weekIndex, label) => {
    const newData = { ...homepageData };
    newData.weeks[weekIndex].label = label;
    setHomepageData(newData);
    setHasChanges(true);
  };

  // Toggle streaming tag
  const toggleStreamingTag = (weekIndex, matchIndex) => {
    const newData = { ...homepageData };
    const match = newData.weeks[weekIndex].matches[matchIndex];
    const hasStreaming = match.tags?.includes('STREAMING');

    if (hasStreaming) {
      match.tags = match.tags.filter((t) => t !== 'STREAMING');
    } else {
      match.tags = [...(match.tags || []), 'STREAMING'];
    }

    setHomepageData(newData);
    setHasChanges(true);
  };

  // Parse pasted table data to extract rows
  const parseBatchInput = (input) => {
    if (!input.trim()) {
      setBatchParsedRows([]);
      return;
    }

    const lines = input.trim().split('\n');
    const rows = [];

    // Check if first line is a header row
    const firstLine = lines[0];
    const firstCells = firstLine.split('\t');

    // Look for a "Session" column in header
    let sessionColIndex = -1;
    let teamColIndex = -1;
    let noteColIndex = -1;
    let dateColIndex = -1;

    // Check if this looks like a header row
    const lowerFirstCells = firstCells.map((c) => c.toLowerCase().trim());
    if (
      lowerFirstCells.includes('session') ||
      lowerFirstCells.includes('sessionkey') ||
      lowerFirstCells.includes('session key')
    ) {
      sessionColIndex = lowerFirstCells.findIndex(
        (c) => c === 'session' || c === 'sessionkey' || c === 'session key'
      );
      teamColIndex = lowerFirstCells.findIndex(
        (c) => c === 'team' || c === 'school'
      );
      noteColIndex = lowerFirstCells.findIndex(
        (c) => c === 'note' || c === 'notes'
      );
      dateColIndex = lowerFirstCells.findIndex((c) => c === 'date');
    }

    // Session key pattern - alphanumeric, 10 chars, may have underscore
    const sessionKeyPattern = /^[A-Za-z0-9_-]{8,12}$/;

    const startLine = sessionColIndex >= 0 ? 1 : 0; // Skip header if found

    for (let i = startLine; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      const cells = line.split('\t');
      let sessionKey = null;
      let displayInfo = [];

      // If we found the session column, use it directly
      if (sessionColIndex >= 0 && cells[sessionColIndex]) {
        const candidate = cells[sessionColIndex].trim();
        if (sessionKeyPattern.test(candidate)) {
          sessionKey = candidate;
        }
        // Build display info from useful columns
        if (teamColIndex >= 0 && cells[teamColIndex]) {
          displayInfo.push(cells[teamColIndex].trim());
        }
        if (dateColIndex >= 0 && cells[dateColIndex]) {
          displayInfo.push(cells[dateColIndex].trim());
        }
        if (noteColIndex >= 0 && cells[noteColIndex]) {
          const note = cells[noteColIndex].trim();
          if (note)
            displayInfo.push(
              note.substring(0, 50) + (note.length > 50 ? '...' : '')
            );
        }
      } else {
        // Fallback: scan cells for something that looks like a session key
        for (const cell of cells) {
          const trimmed = cell.trim();
          if (sessionKeyPattern.test(trimmed)) {
            sessionKey = trimmed;
            break;
          }
        }
        // Use first few cells as display
        displayInfo = cells
          .slice(0, 3)
          .map((c) => c.trim())
          .filter((c) => c && c !== sessionKey);
      }

      if (sessionKey) {
        rows.push({
          sessionKey,
          rawData: displayInfo.join(' | ') || cells.slice(1, 4).join(' | '),
          include: true,
        });
      }
    }

    setBatchParsedRows(rows);
    // Default to the most recent week (last in the list)
    if (rows.length > 0 && homepageData?.weeks?.length > 0) {
      setBatchTargetWeek(homepageData.weeks.length - 1);
    }
  };

  // Handle input change
  const handleBatchInputChange = (e) => {
    const value = e.target.value;
    setBatchInput(value);
    parseBatchInput(value);
  };

  // Toggle row inclusion
  const toggleRowInclude = (index) => {
    const newRows = [...batchParsedRows];
    newRows[index].include = !newRows[index].include;
    setBatchParsedRows(newRows);
  };

  // Fetch sessions and add directly to week
  const fetchAndAddBatchSessions = async () => {
    const keysToFetch = batchParsedRows
      .filter((r) => r.include)
      .map((r) => r.sessionKey);

    if (keysToFetch.length === 0) {
      dispatch(alertActions.notification('No sessions selected'));
      return;
    }

    setIsBatchLoading(true);
    const errors = [];
    let added = 0;
    let skipped = 0;

    const newData = {
      ...homepageData,
      weeks: [...(homepageData?.weeks || [])],
    };
    if (newData.weeks.length === 0) {
      newData.weeks.push({ label: 'Week 1', matches: [], status: 'draft' });
    }
    const weekIndex =
      batchTargetWeek < newData.weeks.length ? batchTargetWeek : 0;

    for (const key of keysToFetch) {
      try {
        const result = await API.graphql(
          graphqlOperation(getSessionByKey, { sessionKey: key })
        );
        const session = result.data?.SessionByKey?.items?.[0];
        if (session) {
          const matchCard = generateMatchCard(session);
          if (matchCard) {
            const exists = newData.weeks[weekIndex].matches.some(
              (m) => m.sessionId === matchCard.sessionId
            );
            if (!exists) {
              newData.weeks[weekIndex].matches.push(matchCard);
              added++;
            } else {
              skipped++;
            }
          }
        } else {
          errors.push(key);
        }
      } catch (e) {
        console.error(`Error fetching ${key}:`, e);
        errors.push(key);
      }
    }

    // Sort by gameTime
    newData.weeks[weekIndex].matches.sort(
      (a, b) => new Date(a.gameTime) - new Date(b.gameTime)
    );

    setHomepageData(newData);
    setHasChanges(true);
    setBatchInput('');
    setBatchParsedRows([]);
    setIsBatchLoading(false);

    if (errors.length > 0) {
      dispatch(
        alertActions.notification(`Could not find: ${errors.join(', ')}`)
      );
    }

    let msg = `Added ${added} matches`;
    if (skipped > 0) msg += `, skipped ${skipped} duplicates`;
    if (errors.length > 0) msg += `, ${errors.length} not found`;
    dispatch(alertActions.success(msg));
  };

  // Get team count for a match
  const getTeamCount = (m) => {
    let count = 0;
    if (m.teamA) count++;
    if (m.teamB) count++;
    if (m.teamC) count++;
    if (m.teamD) count++;
    return count;
  };

  // Filter matches based on search query and active filters
  const matchesSearch = (match) => {
    // Check text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesText =
        match.title?.toLowerCase().includes(query) ||
        match.sessionId?.toLowerCase().includes(query) ||
        match.teamA?.toLowerCase().includes(query) ||
        match.teamB?.toLowerCase().includes(query) ||
        match.teamC?.toLowerCase().includes(query) ||
        match.teamD?.toLowerCase().includes(query);
      if (!matchesText) return false;
    }
    // Check filters
    if (activeFilters.length > 0) {
      const matchTags = match.tags || [];
      const teamCount = getTeamCount(match);

      return activeFilters.some((filter) => {
        // Tag-based filters
        if (['MALE', 'FEMALE', 'STREAMING'].includes(filter)) {
          return matchTags.includes(filter);
        }
        // Meet type filters
        if (filter === 'DUAL') return teamCount === 2;
        if (filter === 'TRI') return teamCount === 3;
        if (filter === 'QUAD') return teamCount >= 4;
        return false;
      });
    }
    return true;
  };

  // Toggle a filter
  const toggleFilter = (filter) => {
    setActiveFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter]
    );
  };

  // Calculate stats for all matches
  const stats = React.useMemo(() => {
    const allMatches = homepageData?.weeks?.flatMap((w) => w.matches) || [];
    const weeks = homepageData?.weeks || [];
    const getWeekStatus = (w) => w.status || (w.published ? 'live' : 'draft');
    return {
      total: allMatches.length,
      mens: allMatches.filter((m) => m.tags?.includes('MALE')).length,
      womens: allMatches.filter((m) => m.tags?.includes('FEMALE')).length,
      streaming: allMatches.filter((m) => m.tags?.includes('STREAMING')).length,
      weeks: weeks.length,
      dual: allMatches.filter((m) => getTeamCount(m) === 2).length,
      tri: allMatches.filter((m) => getTeamCount(m) === 3).length,
      quad: allMatches.filter((m) => getTeamCount(m) >= 4).length,
      live: weeks.filter((w) => getWeekStatus(w) === 'live').length,
      preview: weeks.filter((w) => getWeekStatus(w) === 'preview').length,
      draft: weeks.filter((w) => getWeekStatus(w) === 'draft').length,
    };
  }, [homepageData]);

  // Toggle status filter
  const toggleStatusFilter = (status) => {
    setStatusFilters((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  // Highlight search term in text
  const highlightText = (text) => {
    if (!text || !searchQuery.trim()) return text;
    const query = searchQuery.trim();
    const regex = new RegExp(
      `(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
      'gi'
    );
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="search-highlight">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Save to database - saves to season-specific record
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const seasonId = getSeasonSettingsId(currentSeason);
      const input = {
        id: seasonId,
        settingsType: 'season',
        currentSeason,
        seasonLabel,
        homepageData: JSON.stringify(homepageData),
      };

      if (settingsExist) {
        // Include _version for conflict resolution
        input._version = settingsVersion;
        const result = await updateSettings({ variables: { input } });
        if (result.data?.updateSiteSettings?._version) {
          setSettingsVersion(result.data.updateSiteSettings._version);
        }
      } else {
        const result = await createSettings({ variables: { input } });
        setSettingsExist(true);
        if (result.data?.createSiteSettings?._version) {
          setSettingsVersion(result.data.createSiteSettings._version);
        }
      }

      setHasChanges(false);
      dispatch(alertActions.success(`${seasonLabel} saved!`));
      refetch();
    } catch (e) {
      console.error('Save error:', e);
      dispatch(alertActions.error('Error saving settings: ' + e.message));
    }
    setIsSaving(false);
  };

  // Set this season as the active homepage season
  const setAsActiveSeason = async () => {
    try {
      // Check if global settings exist
      const globalExists = !!globalData?.getSiteSettings;
      const globalVersion = globalData?.getSiteSettings?._version;

      const input = {
        id: GLOBAL_SETTINGS_ID,
        settingsType: 'homepage',
        currentSeason,
        seasonLabel: `Season ${currentSeason}`,
      };

      if (globalExists) {
        input._version = globalVersion;
        await updateSettings({ variables: { input } });
      } else {
        await createSettings({ variables: { input } });
      }

      setActiveSeason(currentSeason);
      dispatch(
        alertActions.success(
          `Season ${currentSeason} is now the active homepage`
        )
      );
    } catch (e) {
      console.error('Error setting active season:', e);
      dispatch(alertActions.error('Error setting active season: ' + e.message));
    }
  };

  if (loadingSettings) {
    return (
      <Container className="homepage-builder">
        <Row className="justify-content-center py-5">
          <Spinner animation="border" />
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="homepage-builder">
      {/* Header */}
      <Row className="builder-header mb-4">
        <Col>
          <h2>Homepage Builder</h2>
          <p className="text-muted mb-0">
            Manage the matches displayed on the public homepage
          </p>
          {homepageData?.weeks?.length > 0 &&
            (() => {
              const getStatus = (w) =>
                w.status || (w.published ? 'live' : 'draft');
              const liveCount =
                homepageData?.weeks?.filter((w) => getStatus(w) === 'live')
                  .length || 0;
              const previewCount =
                homepageData?.weeks?.filter((w) => getStatus(w) === 'preview')
                  .length || 0;
              return (
                <small className="text-muted">
                  {liveCount} live
                  {previewCount > 0 && `, ${previewCount} preview`}
                  {' · '}
                  {liveCount > 0 && (
                    <a href="/" target="_blank" rel="noopener noreferrer">
                      View live
                    </a>
                  )}
                  {liveCount > 0 && previewCount > 0 && ' · '}
                  {previewCount > 0 && (
                    <a
                      href={`/preview?season=${currentSeason}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View preview
                    </a>
                  )}
                </small>
              );
            })()}
        </Col>
        <Col
          xs="auto"
          className="d-flex align-items-center"
          style={{ gap: '0.5rem' }}
        >
          <a
            href={`/preview?season=${currentSeason}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-secondary"
          >
            Preview Season {currentSeason} ↗
          </a>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? (
              <span>
                <Spinner size="sm" animation="border" className="me-2" />
                Saving...
              </span>
            ) : (
              'Save'
            )}
          </Button>
        </Col>
      </Row>

      {hasChanges && (
        <Alert
          variant="warning"
          className="mb-4 d-flex justify-content-between align-items-center"
        >
          <span>You have unsaved changes</span>
          <Button
            variant="link"
            size="sm"
            className="p-0 text-warning"
            onClick={() => setHasChanges(false)}
            title="Dismiss (mark as saved)"
          >
            ×
          </Button>
        </Alert>
      )}

      {/* Season Selector + Import (compact) */}
      <Row className="mb-3 align-items-end">
        <Col xs="auto">
          <Form.Group>
            <Form.Label className="small mb-1">Season</Form.Label>
            <div className="d-flex" style={{ gap: '0.5rem' }}>
              <Form.Control
                as="select"
                size="sm"
                value={currentSeason}
                onChange={(e) => switchSeason(parseInt(e.target.value, 10))}
                style={{ width: '110px' }}
                disabled={isLoadingSeason}
              >
                {availableSeasons.map((s) => (
                  <option key={s} value={s}>
                    Season {s}
                  </option>
                ))}
              </Form.Control>
              {currentSeason !== activeSeason && (
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={setAsActiveSeason}
                  title="Set as the active homepage season"
                >
                  Set Active
                </Button>
              )}
              {currentSeason === activeSeason && (
                <Badge bg="success" className="d-flex align-items-center">
                  Active
                </Badge>
              )}
            </div>
          </Form.Group>
        </Col>
        <Col xs="auto">
          <Form.Group>
            <Form.Label className="small mb-1">Label</Form.Label>
            <Form.Control
              type="text"
              size="sm"
              value={seasonLabel}
              onChange={(e) => {
                setSeasonLabel(e.target.value);
                setHasChanges(true);
              }}
              placeholder="e.g., Season 6"
              style={{ width: '120px' }}
            />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group>
            <Form.Label className="small mb-1">
              Add Sessions{' '}
              <span className="text-muted">(paste session keys or rows)</span>
            </Form.Label>
            <div className="d-flex" style={{ gap: '0.5rem' }}>
              <Form.Control
                as="textarea"
                rows={1}
                size="sm"
                value={batchInput}
                onChange={handleBatchInputChange}
                placeholder="Paste session keys here..."
                style={{
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  resize: 'vertical',
                  minHeight: '31px',
                }}
              />
              {batchParsedRows.length > 0 && (
                <>
                  <Form.Control
                    as="select"
                    size="sm"
                    value={batchTargetWeek}
                    onChange={(e) =>
                      setBatchTargetWeek(parseInt(e.target.value, 10))
                    }
                    style={{ width: '120px', flexShrink: 0 }}
                  >
                    {(homepageData?.weeks?.length || 0) === 0 ? (
                      <option value={0}>Week 1 (new)</option>
                    ) : (
                      [...homepageData.weeks]
                        .map((week, idx) => ({ week, idx }))
                        .reverse()
                        .map(({ week, idx }) => (
                          <option key={idx} value={idx}>
                            {week.label}
                          </option>
                        ))
                    )}
                  </Form.Control>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={fetchAndAddBatchSessions}
                    disabled={
                      isBatchLoading ||
                      batchParsedRows.filter((r) => r.include).length === 0
                    }
                    style={{ flexShrink: 0 }}
                  >
                    {isBatchLoading ? (
                      <Spinner size="sm" animation="border" />
                    ) : (
                      `Add ${batchParsedRows.filter((r) => r.include).length}`
                    )}
                  </Button>
                </>
              )}
            </div>
          </Form.Group>
        </Col>
      </Row>

      {/* Parsed rows preview (only when there are multiple) */}
      {batchParsedRows.length > 1 && (
        <div className="batch-preview mb-3">
          <div className="batch-table" style={{ maxHeight: '150px' }}>
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th style={{ width: '30px' }}></th>
                  <th>Key</th>
                  <th>Info</th>
                </tr>
              </thead>
              <tbody>
                {batchParsedRows.map((row, idx) => (
                  <tr
                    key={idx}
                    className={row.include ? '' : 'text-muted'}
                    style={{ opacity: row.include ? 1 : 0.5 }}
                  >
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={row.include}
                        onChange={() => toggleRowInclude(idx)}
                      />
                    </td>
                    <td>
                      <code style={{ fontSize: '0.75rem' }}>
                        {row.sessionKey}
                      </code>
                    </td>
                    <td>
                      <small className="text-muted">{row.rawData}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Weeks Section */}
      <div className="weeks-container">
        <Row className="align-items-center mb-3">
          <Col className="d-flex align-items-center" style={{ gap: '1rem' }}>
            <h5 className="mb-0">Matches</h5>
            <div className="stats-bar">
              <span
                className="stat-item stat-clickable"
                onClick={() => setActiveFilters([])}
                title="Clear filters"
              >
                {stats.total} total
              </span>
              <span
                className={`stat-item stat-mens stat-clickable ${
                  activeFilters.includes('MALE') ? 'active' : ''
                }`}
                onClick={() => toggleFilter('MALE')}
                title="Filter men's"
              >
                {stats.mens} M
              </span>
              <span
                className={`stat-item stat-womens stat-clickable ${
                  activeFilters.includes('FEMALE') ? 'active' : ''
                }`}
                onClick={() => toggleFilter('FEMALE')}
                title="Filter women's"
              >
                {stats.womens} W
              </span>
              <span
                className={`stat-item stat-streaming stat-clickable ${
                  activeFilters.includes('STREAMING') ? 'active' : ''
                }`}
                onClick={() => toggleFilter('STREAMING')}
                title="Filter streaming"
              >
                {stats.streaming} ▶
              </span>
              <span className="stat-divider">|</span>
              <span
                className={`stat-item stat-dual stat-clickable ${
                  activeFilters.includes('DUAL') ? 'active' : ''
                }`}
                onClick={() => toggleFilter('DUAL')}
                title="Filter dual meets"
              >
                {stats.dual} dual
              </span>
              <span
                className={`stat-item stat-tri stat-clickable ${
                  activeFilters.includes('TRI') ? 'active' : ''
                }`}
                onClick={() => toggleFilter('TRI')}
                title="Filter tri meets"
              >
                {stats.tri} tri
              </span>
              <span
                className={`stat-item stat-quad stat-clickable ${
                  activeFilters.includes('QUAD') ? 'active' : ''
                }`}
                onClick={() => toggleFilter('QUAD')}
                title="Filter quad meets"
              >
                {stats.quad} quad
              </span>
              <span className="stat-divider">|</span>
              <span
                className="stat-item stat-clickable"
                onClick={() => setWeekOrderReversed(!weekOrderReversed)}
                title={
                  weekOrderReversed
                    ? 'Showing oldest first (Week 1 at top) - click to reverse'
                    : 'Showing newest first - click to reverse'
                }
              >
                {stats.weeks} weeks {weekOrderReversed ? '↑' : '↓'}
              </span>
              <span
                className={`stat-item stat-live stat-clickable ${
                  statusFilters.includes('live') ? 'active' : ''
                }`}
                onClick={() => toggleStatusFilter('live')}
                title="Filter live weeks"
              >
                {stats.live} live
              </span>
              <span
                className={`stat-item stat-preview stat-clickable ${
                  statusFilters.includes('preview') ? 'active' : ''
                }`}
                onClick={() => toggleStatusFilter('preview')}
                title="Filter preview weeks"
              >
                {stats.preview} preview
              </span>
              <span
                className={`stat-item stat-draft stat-clickable ${
                  statusFilters.includes('draft') ? 'active' : ''
                }`}
                onClick={() => toggleStatusFilter('draft')}
                title="Filter draft weeks"
              >
                {stats.draft} draft
              </span>
            </div>
          </Col>
          <Col
            xs="auto"
            className="d-flex align-items-center"
            style={{ gap: '0.75rem' }}
          >
            <div className="search-row">
              <Form.Control
                type="text"
                size="sm"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <Button
                  variant="link"
                  className="search-clear"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  ×
                </Button>
              )}
            </div>
            <Button variant="outline-primary" size="sm" onClick={addWeek}>
              + Add Week
            </Button>
          </Col>
        </Row>

        {(homepageData?.weeks?.length || 0) === 0 ? (
          <Alert variant="info">
            No weeks added yet. Add a week to get started.
          </Alert>
        ) : (
          (weekOrderReversed
            ? homepageData.weeks.map((week, i) => ({ week, weekIndex: i }))
            : [...homepageData.weeks].reverse().map((week, i) => ({
                week,
                weekIndex: homepageData.weeks.length - 1 - i,
              }))
          ).map(({ week, weekIndex }) => {
            const status = week.status || (week.published ? 'live' : 'draft');
            // Hide weeks that don't match status filter
            if (statusFilters.length > 0 && !statusFilters.includes(status))
              return null;
            const filteredMatches = week.matches.filter(matchesSearch);
            // Hide weeks with no matching results when searching/filtering
            if (
              (searchQuery || activeFilters.length > 0) &&
              filteredMatches.length === 0
            )
              return null;
            return (
              <Card key={weekIndex} className={`week-card mb-2 week-${status}`}>
                <Card.Header className="d-flex justify-content-between align-items-center py-2 px-3">
                  <div className="d-flex align-items-center">
                    <span
                      className={`week-status ${status}`}
                      onClick={() => cycleWeekStatus(weekIndex)}
                      title="Click to cycle: Draft → Preview → Live"
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={week.label}
                      onChange={(e) =>
                        updateWeekLabel(weekIndex, e.target.value)
                      }
                      className="week-label-input"
                      style={{ width: '120px', marginLeft: '1rem' }}
                    />
                    <span
                      className="text-muted"
                      style={{ marginLeft: '1rem', fontSize: '0.85rem' }}
                    >
                      {searchQuery || activeFilters.length > 0
                        ? `${filteredMatches.length}/${week.matches.length}`
                        : week.matches.length}{' '}
                      match
                      {week.matches.length !== 1 ? 'es' : ''}
                    </span>
                  </div>
                  <Button
                    variant="link"
                    size="sm"
                    className="text-danger p-0"
                    onClick={() => removeWeek(weekIndex)}
                    title="Remove week"
                  >
                    ×
                  </Button>
                </Card.Header>
                <Card.Body className="p-2">
                  {week.matches.length === 0 ? (
                    <p className="text-muted small mb-0">No matches</p>
                  ) : (
                    <div className="matches-grid compact">
                      {week.matches
                        .map((match, matchIndex) => ({ match, matchIndex }))
                        .filter(({ match }) => matchesSearch(match))
                        .map(({ match, matchIndex }) => (
                          <div key={matchIndex} className="match-card">
                            <div className="match-actions">
                              <Button
                                variant="link"
                                size="sm"
                                className="refresh-match"
                                onClick={() =>
                                  refreshMatch(weekIndex, matchIndex)
                                }
                                title="Refresh from session"
                              >
                                ↻
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                className="remove-match"
                                onClick={() =>
                                  removeMatch(weekIndex, matchIndex)
                                }
                                title="Remove match"
                              >
                                ×
                              </Button>
                            </div>
                            <div className="match-title">
                              {highlightText(match.title)}
                            </div>
                            <div className="match-date">
                              {match.gameTime &&
                                format(
                                  new Date(match.gameTime),
                                  "EEE M/dd '@' h:mm a"
                                )}
                            </div>
                            <div className="match-logos">
                              {Array.from({ length: 26 }, (_, i) =>
                                String.fromCharCode(65 + i)
                              )
                                .map((letter) => {
                                  const logo = match[`team${letter}Logo`];
                                  const name = match[`team${letter}`];
                                  return logo ? (
                                    <img
                                      key={letter}
                                      src={logoPath(logo)}
                                      alt={name}
                                      className="team-logo"
                                    />
                                  ) : null;
                                })
                                .filter(Boolean)}
                            </div>
                            <div className="match-tags">
                              {match.tags?.map((tag, i) => (
                                <span
                                  key={i}
                                  className={`tag-pill tag-${tag.toLowerCase()}`}
                                >
                                  {tag === 'MALE'
                                    ? 'MENS'
                                    : tag === 'FEMALE'
                                    ? 'WOMENS'
                                    : tag}
                                </span>
                              ))}
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() =>
                                  toggleStreamingTag(weekIndex, matchIndex)
                                }
                              >
                                {match.tags?.includes('STREAMING')
                                  ? '- Stream'
                                  : '+ Stream'}
                              </Button>
                            </div>
                            {match.multiStream &&
                              Object.keys(match.multiStream).length > 0 && (
                                <div className="match-streams">
                                  <small className="text-muted">Streams:</small>
                                  {Object.keys(match.multiStream).map((key) => (
                                    <span
                                      key={key}
                                      className="stream-pill active"
                                      title={`Click to remove ${match.multiStream[key]} stream`}
                                      onClick={() => {
                                        const newData = {
                                          ...homepageData,
                                        };
                                        const m =
                                          newData.weeks[weekIndex].matches[
                                            matchIndex
                                          ];
                                        const updated = { ...m.multiStream };
                                        delete updated[key];
                                        m.multiStream =
                                          Object.keys(updated).length > 0
                                            ? updated
                                            : undefined;
                                        setHomepageData(newData);
                                        setHasChanges(true);
                                      }}
                                    >
                                      {match.multiStream[key]} ×
                                    </span>
                                  ))}
                                </div>
                              )}
                            <div className="match-key text-muted">
                              <small>{highlightText(match.sessionId)}</small>
                            </div>
                            <div className="match-links">
                              <a
                                href={`/session?s=${match.sessionId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="match-link"
                                title="Open session"
                              >
                                Session
                              </a>
                              <a
                                href={`${SCORESURL}?s=${match.sessionId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="match-link"
                                title="Open scores page"
                              >
                                Scores
                              </a>
                              <a
                                href={`/session/${match.sessionId}/setup`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="match-link"
                                title="Open setup"
                              >
                                Setup
                              </a>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            );
          })
        )}
      </div>
      <ToastStack />
    </Container>
  );
}

export default HomepageBuilder;
